import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { Download, ExternalLink, FilePlus, FileText, Loader2, Sparkles, Trash2 } from "lucide-react";

import { HubBlobUrlPreview } from "@/components/HubBlobUrlPreview";
import { HubPdfDocumentView } from "@/components/HubPdfDocumentView";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { hubFillPreviewFrameClassName, hubInlinePreviewFrameClassName } from "@/lib/hubLayoutClassNames";
import { freshdeskTicketUrl } from "@/lib/freshdeskAgentUrl";
import { inferHubFilePreviewKind } from "@/lib/hubFilePreview";
import { cn } from "@/lib/utils";
import type { ApInvoiceDocumentRow } from "./apInvoicesApi";
import { ApInvoiceSpreadsheetPreview } from "./ApInvoiceSpreadsheetPreview";
import { downloadInvoiceDocumentFile } from "./downloadInvoiceDocument";

const AP_INVOICE_ACCEPT =
  "application/pdf,image/png,image/jpeg,image/webp,text/html,text/csv,.csv,.xlsx,.xlsm,.eml,.html";

const AP_INVOICE_TOOLBAR_BUTTON_CLASS =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50";

function isPdfFile(file: File): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function isImageFile(file: File): boolean {
  return file.type.startsWith("image/") || /\.(png|jpe?g|webp)$/i.test(file.name);
}

function isHtmlFile(file: File): boolean {
  return file.type === "text/html" || /\.(html?|eml)$/i.test(file.name);
}

function isPdfFilename(filename: string | null): boolean {
  return filename != null && filename.toLowerCase().endsWith(".pdf");
}

function isImageFilename(filename: string | null): boolean {
  return filename != null && /\.(png|jpe?g|webp)$/i.test(filename);
}

function isHtmlFilename(filename: string | null, contentType?: string | null): boolean {
  const type = (contentType ?? "").toLowerCase();
  if (type === "text/html" || type === "message/rfc822") return true;
  return filename != null && /\.(html?|eml)$/i.test(filename);
}

function isSpreadsheetFilename(filename: string | null, contentType?: string | null): boolean {
  const type = (contentType ?? "").toLowerCase();
  if (type.includes("spreadsheetml") || type.includes("sheet.macroenabled")) return true;
  return filename != null && /\.(xlsx|xlsm)$/i.test(filename);
}

export type ApInvoiceDocumentPanelLayout = "inline" | "compact" | "fill";

export function ApInvoiceDocumentPanel({
  invoiceId,
  file,
  previewUrl,
  filename,
  contentType,
  emailPreviewHtml = null,
  documents = [],
  selectedDocumentId,
  onSelectDocument,
  freshdeskTicketId,
  onFileChange,
  onRemove,
  onAiReview,
  showAiReviewButton = false,
  isAiReviewStarting = false,
  disabled = false,
  isUploading = false,
  compact = false,
  layout,
  splitSuggestionBadge = null,
}: {
  invoiceId?: string;
  file: File | null;
  previewUrl: string | null;
  filename: string | null;
  contentType?: string | null;
  emailPreviewHtml?: string | null;
  documents?: ApInvoiceDocumentRow[];
  selectedDocumentId?: string | null;
  onSelectDocument?: (documentId: string) => void;
  freshdeskTicketId?: number | null;
  onFileChange: (file: File | null) => void;
  onRemove: () => void;
  onAiReview?: () => void;
  showAiReviewButton?: boolean;
  isAiReviewStarting?: boolean;
  disabled?: boolean;
  isUploading?: boolean;
  compact?: boolean;
  layout?: ApInvoiceDocumentPanelLayout;
  /** Shown when multiple attachments or filename heuristic suggests multiple invoices. */
  splitSuggestionBadge?: string | null;
}) {
  const { showToast } = useHubToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [localHtmlPreview, setLocalHtmlPreview] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (file == null || !isHtmlFile(file)) {
      setLocalHtmlPreview(null);
      return;
    }
    let cancelled = false;
    void file.text().then((text) => {
      if (!cancelled) setLocalHtmlPreview(text);
    });
    return () => {
      cancelled = true;
    };
  }, [file]);

  const hasDocument = previewUrl != null || file != null || filename != null || documents.length > 0;

  const applyFile = useCallback(
    (next: File | null) => {
      onFileChange(next);
      if (fileInputRef.current != null) {
        fileInputRef.current.value = "";
      }
    },
    [onFileChange],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      event.stopPropagation();
      setDragActive(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      const dropped = event.dataTransfer.files?.[0];
      if (dropped != null) {
        applyFile(dropped);
      }
    },
    [applyFile, disabled],
  );

  const handleZoneClick = useCallback(() => {
    if (disabled || hasDocument) return;
    fileInputRef.current?.click();
  }, [disabled, hasDocument]);

  const handleOpenPreview = useCallback(() => {
    if (previewUrl != null) {
      window.open(previewUrl, "_blank", "noopener,noreferrer");
    }
  }, [previewUrl]);

  const handleDownload = useCallback(() => {
    if (previewUrl == null && file == null) return;
    setIsDownloading(true);
    void downloadInvoiceDocumentFile({
      file,
      url: previewUrl,
      filename: file?.name ?? filename,
    })
      .catch((error: unknown) => {
        showToast({
          tone: "error",
          message: error instanceof Error ? error.message : "Could not download the invoice document.",
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }, [file, filename, previewUrl, showToast]);

  const displayName = file?.name ?? filename;
  const pdf = (file != null && isPdfFile(file)) || (file == null && isPdfFilename(filename));
  const image = (file != null && isImageFile(file)) || (file == null && isImageFilename(filename));
  const html =
    (file != null && isHtmlFile(file)) || (file == null && isHtmlFilename(filename, contentType));
  const spreadsheet =
    (file != null && isSpreadsheetFilename(file.name, file.type)) ||
    (file == null && isSpreadsheetFilename(filename, contentType));
  const textPreviewKind =
    displayName != null ? inferHubFilePreviewKind(displayName, contentType ?? file?.type ?? null) : "none";
  const htmlSrcDoc = localHtmlPreview ?? emailPreviewHtml;

  const panelLayout: ApInvoiceDocumentPanelLayout = layout ?? (compact ? "compact" : "inline");
  const fill = panelLayout === "fill";
  const compactLayout = panelLayout === "compact";

  const emptyZoneMinHeight = compactLayout ? "min-h-28" : fill ? "min-h-0 flex-1" : "min-h-[min(85vh,32rem)]";
  const previewMaxHeight = compactLayout ? "max-h-40" : fill ? "max-h-full min-h-0 flex-1" : "max-h-[min(85vh,52rem)]";
  const pdfPreviewHeight = compactLayout
    ? "h-40"
    : fill
      ? hubFillPreviewFrameClassName()
      : hubInlinePreviewFrameClassName();
  const fallbackMinHeight = compactLayout ? "min-h-28" : fill ? "min-h-0 flex-1" : "min-h-[min(85vh,32rem)]";
  const emptyPadding = compactLayout ? "py-6" : fill ? "py-6" : "py-10";
  const ticketUrl =
    freshdeskTicketId != null && freshdeskTicketId > 0 ? freshdeskTicketUrl(freshdeskTicketId) : null;

  return (
    <Card className={cn("min-w-0", fill && "flex min-h-0 flex-1 flex-col overflow-hidden")}>
      <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <CardTitle>Invoice documents</CardTitle>
          {ticketUrl != null ? (
            <p className="text-xs text-slate-600">
              Freshdesk ticket{" "}
              <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:underline">
                {freshdeskTicketId}
              </a>
            </p>
          ) : null}
          {documents.length > 0 ? (
            <Badge tone="neutral">{documents.length} attachment{documents.length === 1 ? "" : "s"}</Badge>
          ) : null}
          {splitSuggestionBadge != null && splitSuggestionBadge !== "" ? (
            <Badge tone="warning">{splitSuggestionBadge}</Badge>
          ) : null}
        </div>
        {hasDocument ? (
          <div className="flex items-center gap-1">
            {showAiReviewButton && onAiReview != null ? (
              <HoverHintTrigger
                trigger={
                  <button
                    type="button"
                    className={cn(
                      AP_INVOICE_TOOLBAR_BUTTON_CLASS,
                      "hover:border-violet-200 hover:bg-violet-50 hover:text-violet-800",
                    )}
                    aria-label="Run AI review on invoice documents"
                    disabled={disabled || isUploading || isAiReviewStarting}
                    onClick={onAiReview}
                  >
                    {isAiReviewStarting ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    ) : (
                      <Sparkles className="h-4 w-4" aria-hidden />
                    )}
                  </button>
                }
              >
                Extract vendor, date, purchase order, tracking, and line items from the PDF, spreadsheet, CSV, and email
                using Gemini. You can verify the filled fields before submit.
              </HoverHintTrigger>
            ) : null}
            <HoverHintTrigger
              trigger={
                <button
                  type="button"
                  className={AP_INVOICE_TOOLBAR_BUTTON_CLASS}
                  aria-label="Add another invoice file"
                  disabled={disabled || isUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FilePlus className="h-4 w-4" aria-hidden />
                </button>
              }
            >
              Add another PDF, image, CSV, Excel workbook, or email file. The viewer can switch among files.
            </HoverHintTrigger>
            <HoverHintTrigger
              trigger={
                <button
                  type="button"
                  className={cn(
                    AP_INVOICE_TOOLBAR_BUTTON_CLASS,
                    "hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700",
                  )}
                  aria-label="Remove selected invoice document"
                  disabled={disabled || isUploading}
                  onClick={onRemove}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              }
            >
              Remove the selected invoice document.
            </HoverHintTrigger>
            <HoverHintTrigger
              trigger={
                <button
                  type="button"
                  className={AP_INVOICE_TOOLBAR_BUTTON_CLASS}
                  aria-label="Open invoice preview in a new window"
                  disabled={previewUrl == null}
                  onClick={handleOpenPreview}
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                </button>
              }
            >
              Open the selected file in a new browser tab.
            </HoverHintTrigger>
            <HoverHintTrigger
              trigger={
                <button
                  type="button"
                  className={AP_INVOICE_TOOLBAR_BUTTON_CLASS}
                  aria-label="Download invoice document"
                  disabled={(previewUrl == null && file == null) || isDownloading}
                  onClick={handleDownload}
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Download className="h-4 w-4" aria-hidden />
                  )}
                </button>
              }
            >
              Download the selected file to this computer.
            </HoverHintTrigger>
          </div>
        ) : null}
      </CardHeader>
      <CardBody className={cn("space-y-3", fill && "flex min-h-0 flex-1 flex-col overflow-hidden")}>
        {documents.length > 1 && onSelectDocument != null ? (
          <ul className="max-h-40 shrink-0 space-y-1 overflow-y-auto">
            {documents.map((document) => (
              <li key={document.id} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onSelectDocument(document.id)}
                  className={cn(
                    "min-w-0 flex-1 truncate rounded-md px-2 py-1 text-left text-sm",
                    (selectedDocumentId ?? documents[0]?.id) === document.id
                      ? "bg-brand-50 font-medium text-brand-800"
                      : "text-slate-600 hover:bg-slate-50",
                  )}
                >
                  <span className="text-slate-500">{document.kind_label}:</span> {document.filename}
                </button>
              </li>
            ))}
          </ul>
        ) : documents.length === 1 ? (
          <p className="shrink-0 text-xs text-slate-500">
            {documents[0].kind_label}: {documents[0].filename}
          </p>
        ) : null}
        <input
          ref={fileInputRef}
          id="ap-add-invoice-file"
          type="file"
          accept={AP_INVOICE_ACCEPT}
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            applyFile(event.target.files?.[0] ?? null);
          }}
        />
        <div
          className={cn(
            fill && hasDocument ? "flex min-h-0 flex-1 flex-col overflow-hidden" : "",
            !hasDocument ? "" : "space-y-2",
          )}
        >
          {hasDocument && documents.length > 1 ? (
            <div className="shrink-0 border-b border-slate-200 px-1 pb-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Preview</h3>
            </div>
          ) : null}
          <div
            role={!hasDocument ? "button" : undefined}
            tabIndex={!hasDocument ? 0 : undefined}
            aria-label={!hasDocument ? "Upload invoice document" : undefined}
            onClick={handleZoneClick}
            onKeyDown={(event) => {
              if (!hasDocument && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              "overflow-hidden rounded-lg border-2 border-dashed transition-colors",
              fill && "flex min-h-0 flex-1 flex-col",
              dragActive
                ? "border-brand-500 bg-brand-50"
                : hasDocument
                  ? "border-slate-200 bg-slate-50"
                  : "cursor-pointer border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100/80 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
              !hasDocument ? emptyZoneMinHeight : "",
              fill && hasDocument ? "min-h-0 flex-1 basis-0 flex-col overflow-hidden" : "",
            )}
          >
          {previewUrl != null && image ? (
            <img
              src={previewUrl}
              alt="Vendor invoice preview"
              className={cn("mx-auto w-full object-contain", previewMaxHeight, fill && "flex-1")}
            />
          ) : html && htmlSrcDoc != null ? (
            <iframe
              title="Vendor invoice email preview"
              srcDoc={htmlSrcDoc}
              sandbox="allow-same-origin allow-popups allow-downloads"
              className={cn("w-full bg-white", pdfPreviewHeight)}
            />
          ) : html && file == null ? (
            <div className={cn("flex items-center justify-center px-4 text-sm text-slate-500", pdfPreviewHeight)}>
              Loading email preview…
            </div>
          ) : previewUrl != null && pdf ? (
            <HubPdfDocumentView
              title="Vendor invoice PDF preview"
              url={previewUrl}
              className={cn("w-full bg-white", pdfPreviewHeight)}
            />
          ) : previewUrl != null && spreadsheet && invoiceId != null && invoiceId !== "" && selectedDocumentId != null ? (
            <ApInvoiceSpreadsheetPreview
              invoiceId={invoiceId}
              documentId={selectedDocumentId}
              fillAvailableHeight={fill}
              frameClassName={pdfPreviewHeight}
            />
          ) : previewUrl != null && textPreviewKind === "text" ? (
            <HubBlobUrlPreview
              previewUrl={previewUrl}
              filename={displayName ?? "invoice-file"}
              contentType={contentType ?? file?.type ?? null}
              fillAvailableHeight={fill}
            />
          ) : hasDocument ? (
            <div className={cn("flex items-center gap-3 px-4 py-4 text-sm text-slate-600", fallbackMinHeight)}>
              <FileText className="h-8 w-8 shrink-0 text-slate-400" aria-hidden />
              <div>
                {displayName != null ? <p className="font-medium text-slate-900">{displayName}</p> : null}
                <p>Preview is not available for this file type. Drop another file here to add it.</p>
              </div>
            </div>
          ) : (
            <div
              className={cn(
                "flex flex-col items-center justify-center px-4 text-center text-sm text-slate-500",
                emptyZoneMinHeight,
                emptyPadding,
              )}
            >
              <FileText className="mb-3 h-10 w-10 text-slate-300" aria-hidden />
              <p className="font-medium text-slate-700">Drop invoice here or click to upload</p>
              <p className="mt-1 text-xs text-slate-500">PDF, PNG, JPEG, WebP, CSV, Excel, or email HTML</p>
              {isUploading ? (
                <p className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  Uploading…
                </p>
              ) : null}
            </div>
          )}
          {isUploading && hasDocument ? (
            <div className="flex items-center justify-center gap-2 border-t border-slate-200 bg-white/90 px-3 py-2 text-xs text-slate-600">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Uploading invoice document…
            </div>
          ) : null}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
